import { jsx as _jsx } from "react/jsx-runtime";
import { AxiosError } from 'axios';
import { createContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useLocalizeMessage, Spinner, useToastNotification } from 'libs.nucleus';
import { usePersistentValue, } from 'libs.react';
import { ApiClientService, useApiClient } from '../../api_client';
import { AuthEndpoint, AuthRoute } from '../types';
const isMedableIdentity = (identity) => {
    return identity.email.endsWith('@medable.com');
};
const missingAuthProvider = () => {
    throw Error('Missing the auth provider');
};
export const AuthContext = createContext({
    entity: undefined,
    entityId: 0,
    forgotPassword: missingAuthProvider,
    getIDPConfiguration: missingAuthProvider,
    isInternalUser: false,
    isLoading: true,
    isLoggedIn: false,
    lastEntities: [],
    login: missingAuthProvider,
    logout: missingAuthProvider,
    navigateToLogin: missingAuthProvider,
    redirectToDefaultPage: missingAuthProvider,
    refreshEntity: missingAuthProvider,
    refreshIdentity: missingAuthProvider,
    resetPassword: missingAuthProvider,
    scopes: {},
    setEntity: missingAuthProvider,
    ssoLogin: missingAuthProvider,
    user: undefined,
    roleGroups: [],
    userEntities: [],
});
const iamServiceEndPoint = process.env.IAM_SERVICE_ENDPOINT;
const ENTITY_INFO_KEY = 'AUTH_LOGIN_ENTITIES';
/**
 * The AuthProvider is the main context provider to handle the user information and authentication
 * It communicates with the IAM service to validate the user credentials and store the token
 */
export const AuthProvider = ({ children }) => {
    const iamClient = useApiClient(ApiClientService.IAM);
    const momClient = useApiClient(ApiClientService.MOM);
    const { addNotification } = useToastNotification();
    const translate = useLocalizeMessage();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [lastEntities, setLastEntities] = usePersistentValue(ENTITY_INFO_KEY, []);
    const [user, setUser] = useState();
    const [scopes, setScopes] = useState({});
    const [roleGroups, setRoleGroups] = useState([]);
    const [isInternalUser, setIsInternalUser] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [entityId, setEntityId] = useState(0);
    const [entity, setEntity] = useState();
    const [userEntities, setUserEntities] = useState([]);
    useEffect(() => {
        refreshIdentity();
    }, []);
    useEffect(() => {
        const errorDescription = searchParams.get('error_description');
        if (errorDescription) {
            addNotification({
                title: translate('Error'),
                subtitle: errorDescription,
                type: 'error',
            });
            searchParams.delete('error_description');
            setSearchParams(searchParams);
        }
        if (searchParams.get('expired-session') === 'true') {
            cleanUserInformation();
            searchParams.delete('expired-session');
            setSearchParams(searchParams);
        }
    }, [searchParams]);
    const refreshIdentity = async () => {
        setIsLoading(true);
        try {
            const { data } = await iamClient.get(AuthEndpoint.ME);
            await getEntities(data.loginEntity);
            saveLastEntity(data.loginEntity, data.identity.email);
            verifyCustomDomain(data.customDomain);
            if (data && data.identity) {
                setIsInternalUser(isMedableIdentity(data.identity));
                setIsLoggedIn(true);
                setUser(data.identity);
                setRoleGroups(data.roleGroups);
                setScopes(data.scopes);
            }
            else {
                cleanUserInformation();
            }
        }
        catch {
            cleanUserInformation();
        }
        finally {
            setIsLoading(false);
            setIsInitialized(true);
        }
    };
    const refreshEntity = async (id = entityId) => {
        try {
            const { data: response } = await momClient.get(`/v1/entities/${id}`);
            setEntity(response.data);
            setEntityId(response.data.id);
        }
        catch (error) {
            console.error('Error getting entities: ', error);
            setEntityId(0);
        }
    };
    const getEntities = async (newEntityId) => {
        setEntityId(newEntityId);
        try {
            refreshEntity(newEntityId); // We dont await so it runs in parallel
            const { data: response } = await momClient.get('/v1/entities', {
                params: { hasEntityAccess: true },
            });
            setUserEntities(response.data || []);
        }
        catch (error) {
            console.error('Error getting entities: ', error);
            setUserEntities([]);
        }
    };
    const saveLastEntity = (newEntityId, email) => {
        try {
            const newLastEntities = lastEntities.filter((entityInfo) => entityInfo.email !== email);
            setLastEntities([...newLastEntities, { entityId: newEntityId, email }]);
        }
        catch (error) {
            console.error('Error saving last entities information', error);
        }
    };
    /**
     * Checks the IDPs associated to the given email and returns those
     */
    const getIDPConfiguration = async (email) => {
        setIsLoading(true);
        try {
            const { data: response } = await iamClient.get(`/v1/sso/check-username`, {
                params: { email, loginMethods: ['all'] },
            });
            setIsLoading(false);
            return response.data;
        }
        catch (error) {
            console.error('Error getting IDP configuration: ', error);
            setIsLoading(false);
        }
        return [];
    };
    const cleanUserInformation = () => {
        setUser(undefined);
        setIsInternalUser(false);
        setIsLoggedIn(false);
        setEntityId(0);
        setScopes({});
        setRoleGroups([]);
    };
    /**
     * Used to log the user in the application and set the token and user identity information
     */
    const login = async (email, password, entityId) => {
        setIsLoading(true);
        try {
            const lastEntity = lastEntities.find((entityInfo) => entityInfo.email === email);
            const { data } = await iamClient.post(AuthEndpoint.LOGIN, {
                email,
                password,
                entityId: entityId || lastEntity?.entityId,
            });
            await getEntities(data.loginEntity);
            saveLastEntity(data.loginEntity, data.identity.email);
            verifyCustomDomain(data.customDomain);
            setIsInternalUser(isMedableIdentity(data.identity));
            setIsLoggedIn(true);
            setUser(data.identity);
            setScopes(data.scopes);
            setRoleGroups(data.roleGroups);
        }
        catch (err) {
            if (err instanceof AxiosError && !err.response?.data.errorHandled) {
                addNotification({
                    title: 'Error trying to login',
                    subtitle: err.response?.data.message || 'There was an error logging in. Please try again',
                    type: 'error',
                });
            }
            throw err;
        }
        finally {
            setIsLoading(false);
        }
    };
    /**
     * Removes any stored user identity information and calls to invalidate the token on the IAM service
     */
    const logout = async () => {
        await iamClient.post(AuthEndpoint.LOGOUT);
        cleanUserInformation();
    };
    /**
     * Triggers the forgot password flow, that will send an email to the user with a link to reset the password
     */
    const forgotPassword = async (email) => {
        setIsLoading(true);
        try {
            await iamClient.post(AuthEndpoint.FORGOT_PASSWORD, { email });
        }
        catch (err) {
            if (err instanceof AxiosError && !err.response?.data.errorHandled) {
                addNotification({
                    title: 'Error trying to send reset password link',
                    subtitle: err.response?.data.message || 'There was an error trying to send the reset password link. Please try again',
                    type: 'error',
                });
            }
        }
        finally {
            setIsLoading(false);
        }
    };
    /**
     * Triggers the SSO login flow, allowing the user to login using some configured IDP service
     */
    const ssoLogin = (idpConfiguration, email, txn) => {
        setIsLoading(true);
        const url = `${iamServiceEndPoint}${AuthEndpoint.SSO_LOGIN}?`;
        const location = searchParams.has('location') ? `?location=${searchParams.get('location')}` : undefined;
        const redirect = searchParams.get('redirect');
        // If `redirect` is an Agents url, use it. Otherwise, use the current origin with the redirect or location or root
        const return_to = redirect && process.env.AGENT_STUDIO_URL && redirect.startsWith(process.env.AGENT_STUDIO_URL)
            ? redirect
            : `${window.location.origin}${redirect || location || '/'}`;
        const newSearchParams = new URLSearchParams({
            idp: idpConfiguration.key,
            return_to,
            email,
            ...(txn && { txn }),
        });
        document.location.href = `${url}${newSearchParams.toString()}`;
    };
    /**
     * Used to reset the user password, normally used when the user has received an email to reset the password
     * either if it is to reset the password or to set the password by the first time
     */
    const resetPassword = async (password, token) => {
        setIsLoading(true);
        try {
            await iamClient.post(`${AuthEndpoint.RESET_PASSWORD}${token}`, { password });
            addNotification({
                title: translate('Reset password'),
                subtitle: translate('Your new password has been set. You may now sign in.'),
                type: 'success',
            });
        }
        catch (err) {
            if (err instanceof AxiosError && !err.response?.data.errorHandled) {
                addNotification({
                    title: 'Error trying to reset password',
                    subtitle: err.response?.data.message || 'There was an error trying to reset the password. Please try again',
                    type: 'error',
                });
            }
        }
        finally {
            setIsLoading(false);
        }
    };
    const navigateToLogin = () => {
        if (pathname !== AuthRoute.LOGIN) {
            navigate(AuthRoute.LOGIN);
        }
    };
    const isValidMedableRedirectUrl = (url) => {
        //  validate if the url is a valid medable redirect url
        const allowedDomains = ['medable.com', 'medable.cn'];
        try {
            const { hostname } = new URL(url);
            return allowedDomains.some((domain) => hostname.endsWith(domain));
        }
        catch {
            return false;
        }
    };
    /**
     * Redirects the user to the default page or to any page that was set in the query params
     */
    const redirectToDefaultPage = async () => {
        const locationParam = searchParams.get('location');
        if (locationParam) {
            const { data: response } = await iamClient.get('/v1/gitbook/doc-url');
            if (response.url) {
                const url = new URL(response.url);
                window.location.href = `${url.origin}${url.pathname}/${locationParam}${url.search}`;
            }
        }
        else {
            const redirectParam = searchParams.get('redirect');
            if (redirectParam && isValidMedableRedirectUrl(redirectParam)) {
                window.location.href = redirectParam;
            }
            else {
                navigate(redirectParam || '/');
            }
        }
    };
    const verifyCustomDomain = (customDomain) => {
        if (process.env.STUDIO_ENV !== 'local' && customDomain && customDomain !== window.location.origin) {
            window.location.href =
                pathname === AuthRoute.LOGIN || pathname.includes(AuthRoute.SWITCH_WORKSPACE)
                    ? customDomain
                    : `${customDomain}${pathname}${window.location.search}`;
        }
    };
    const providerValue = useMemo(() => ({
        entity,
        entityId,
        forgotPassword,
        getIDPConfiguration,
        isInternalUser,
        isLoading,
        isLoggedIn,
        lastEntities,
        login,
        logout,
        navigateToLogin,
        redirectToDefaultPage,
        refreshIdentity,
        resetPassword,
        refreshEntity,
        scopes,
        setEntity,
        ssoLogin,
        user,
        roleGroups,
        userEntities,
    }), [
        entity,
        entityId,
        forgotPassword,
        getIDPConfiguration,
        isInternalUser,
        isLoading,
        isLoggedIn,
        lastEntities,
        login,
        logout,
        navigateToLogin,
        redirectToDefaultPage,
        refreshEntity,
        refreshIdentity,
        resetPassword,
        scopes,
        setEntity,
        ssoLogin,
        user,
        roleGroups,
        userEntities,
    ]);
    return (_jsx(AuthContext.Provider, { value: providerValue, children: isInitialized ? (children) : (_jsx("div", { className: 'w-full h-full flex items-center justify-center flex-1', children: _jsx(Spinner, { size: 'xl' }) })) }));
};
